/* eslint-disable jsx-a11y/alt-text, react/jsx-no-target-blank */
import React, { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import './App.css';
import ContentLayout from './ContentLayout';

import Links from './Links';

function FamilyTreePerson({
  className = '',
  name,
  dates,
  role,
  note,
}) {
  return (
    <div className={`edward-family-person ${className}`}>
      <strong>{name}</strong>
      <span>{dates}</span>
      {role && <em>{role}</em>}
      {note && <small>{note}</small>}
    </div>
  );
}

function FamilyTreeUnion({
  className = '',
  label,
  description,
}) {
  return (
    <div className={`edward-family-union ${className}`} aria-label={description}>
      <span>{label}</span>
    </div>
  );
}

function EdwardImmediateFamilyTree() {
  return (
    <figure className="edward-family-tree-card">
      <div className="edward-family-tree" aria-label="Edward Malam immediate family tree">
        <div className="edward-family-title">
          Edward Malam &ndash; immediate family
        </div>

        <FamilyTreePerson
          className="edward-family-person-thomas"
          name="Thomas Malam"
          dates="1807–1832"
          role="father"
        />

        <FamilyTreePerson
          className="edward-family-person-emma"
          name="Emma Taylor"
          dates="about 1812–1880"
          role="mother"
        />

        <FamilyTreePerson
          className="edward-family-person-webb"
          name="John Webb"
          dates="about 1811–1839"
          role="step-father"
        />

        <FamilyTreeUnion
          className="edward-family-union-parents"
          label="m. 1"
          description="First marriage: Thomas Malam and Emma Taylor"
        />

        <FamilyTreeUnion
          className="edward-family-union-step"
          label="m. 2"
          description="Second marriage: Emma Taylor and John Webb"
        />

        <div className="edward-family-line-descent" aria-hidden="true">
          <span />
          <span />
        </div>

        <FamilyTreePerson
          className="edward-family-person-samuel"
          name="Samuel Finney"
          dates="1854–?"
          role=""
        />

        <FamilyTreePerson
          className="edward-family-person-clarissa"
          name="Clarissa Finney"
          dates="1856–1928"
          role="wife"
          note="née Bird"
        />

        <FamilyTreePerson
          className="edward-family-person-edward"
          name="Edward Malam"
          dates="about 1829–1909"
          role=""
        />

        <FamilyTreePerson
          className="edward-family-person-edna"
          name="Edna Malam"
          dates="about 1831–1902"
          role="sister"
        />

        <FamilyTreeUnion
          className="edward-family-union-clarissa-first"
          label="m. 1"
          description="Clarissa Bird's first marriage: Samuel Finney and Clarissa Bird"
        />

        <FamilyTreeUnion
          className="edward-family-union-clarissa-second"
          label="m. 2"
          description="Clarissa Bird's second marriage: Clarissa Bird and Edward Malam"
        />
      </div>
      <figcaption>Edward Malam's immediate family (parents, step-father, sister, wife)</figcaption>
    </figure>
  );
}

function RelationshipChartPerson({
  className = '',
  name,
  dates,
  relation,
  tone = 'blue',
  connects = false,
}) {
  const classes = [
    'edward-relationship-person',
    `edward-relationship-person-${tone}`,
    connects ? 'edward-relationship-person-connects' : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div className={classes}>
      <strong>{name}</strong>
      {dates && <span>{dates}</span>}
      {relation && <em>{relation}</em>}
    </div>
  );
}

function EdwardRelationshipChart() {
  return (
    <figure id="edward-relationship-chart" className="edward-relationship-card">
      <div className="edward-relationship-chart" aria-label="Relationship chart showing the common ancestor of Edward Malam and John Malam">
        <RelationshipChartPerson
          className="edward-relationship-common"
          name="Thomas Malam"
          dates="1744–1821"
          relation="great-great-great-great-grandfather"
          tone="red"
        />

        <div className="edward-relationship-split-line" aria-hidden="true">
          <span />
          <span />
        </div>

        <div className="edward-relationship-branch-label edward-relationship-branch-label-edward">
          Edward Malam branch
        </div>

        <div className="edward-relationship-branch-label edward-relationship-branch-label-john">
          John Malam branch
        </div>

        <RelationshipChartPerson
          className="edward-relationship-edward-branch-one"
          name="Thomas Malam"
          dates="1770–1829"
          relation="great-great-great-uncle"
          connects
        />

        <RelationshipChartPerson
          className="edward-relationship-edward-branch-two"
          name="Thomas Malam"
          dates="1807–1832"
          relation="first cousin four times removed up"
          connects
        />

        <RelationshipChartPerson
          className="edward-relationship-edward-branch-three"
          name="Edward Malam"
          dates="about 1829–1909"
          relation="second cousin three times removed up"
          tone="red"
        />

        <RelationshipChartPerson
          className="edward-relationship-john-branch-one"
          name="John Malam"
          dates="1772–1851"
          relation="great-great-great-grandfather"
          connects
        />

        <RelationshipChartPerson
          className="edward-relationship-john-branch-two"
          name="William Malam"
          dates="1810–1887"
          relation="great-great-grandfather"
          connects
        />

        <RelationshipChartPerson
          className="edward-relationship-john-branch-three"
          name="John Jones Malam"
          dates="1842–1919"
          relation="great-grandfather"
          connects
        />

        <RelationshipChartPerson
          className="edward-relationship-john-branch-four"
          name="George Malam"
          dates="1884–1957"
          relation="grandfather"
          connects
        />

        <RelationshipChartPerson
          className="edward-relationship-john-branch-five"
          name="Brian Malam"
          dates="1913–1991"
          relation="father"
          connects
        />

        <RelationshipChartPerson
          className="edward-relationship-john-branch-six"
          name="John Malam"
          relation="me"
          tone="red"
          connects
        />

        <RelationshipChartPerson
          className="edward-relationship-john-branch-seven"
          name="Joseph Malam"
          relation="son"
        />

        {/* <div className="edward-relationship-chart-title">
          Relationship chart<br />
          John Malam to<br />
          Edward Malam
        </div> */}
      </div>
      <figcaption>Relationship chart, showing how Thomas Malam (1744&mdash;1821) is the common ancestor of both Edward Malam and John Malam</figcaption>
    </figure>
  );
}

const edwardSections = [
  { id: 'early-life', part: 'early-life', label: 'Early life and drapery', to: '/edwardmalam' },
  { id: 'brewery', part: 'brewery', label: 'Hanley Brewery', to: '/edwardmalam/brewery' },
  { id: 'family', part: 'family', label: 'Marriage and homes', to: '/edwardmalam/family' },
  { id: 'will', part: 'will', label: 'Will and heirs', to: '/edwardmalam/will' },
  { id: 'legacy', part: 'legacy', label: 'Legacy', to: '/edwardmalam/legacy' },
];

function EdwardMalam() {
  const { part } = useParams();
  const currentEdwardPart = edwardSections.some((section) => section.part === part)
    ? part
    : 'early-life';
  const currentEdwardSectionIndex = edwardSections.findIndex((section) => (
    section.part === currentEdwardPart
  ));
  const previousEdwardSection = edwardSections[currentEdwardSectionIndex - 1];
  const nextEdwardSection = edwardSections[currentEdwardSectionIndex + 1];

  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const toggleDropdown = () => setIsDropdownOpen(!isDropdownOpen);
  const showDropdown = () => { setIsDropdownOpen(true); };
  const hideDropdown = () => { setIsDropdownOpen(false); };

  const [lightboxImageSrc, setLightboxImageSrc] = useState('');

  const openLightbox = (src) => {
    setLightboxImageSrc(src);
  };

  const closeLightbox = () => {
    setLightboxImageSrc('');
  };

  return (
    <ContentLayout
      title="Edward Malam"
      titleSuffix="c.1829—1909"
      intro="A philanthropist for the people of Stoke-on-Trent"
      showIntro
      sectionNav={edwardSections}
      heroImage="/banners/edward-malam-letterhead.jpg"
      heroAlt="Edward Malam Hanley Brewery letterhead"
      heroPosition="center 48%"
      className="edward-content-shell"
    >
      <div className="content-body legacy-content-page edward-page">
        <div className="flex flex-row max-[768px]:bg-[#f8f7ff] text-[#1f1f1f]">

          <div className='max-[768px]:hidden w-[22rem]'>
            <Links />
          </div>

          <div className='flex flex-col'>
            <div className='flex flex-row justify-between'>
              <Link to="/" className='min-[768px]:hidden'>
                <svg className="mx-4 mt-6" id="text" width="140" viewBox="0 0 333 237" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M68 18.5C68.8922 18.5991 68.7432 20.401 68.75 21C68.7673 22.5226 68.9175 23.8936 69.1944 25.3889C69.3412 26.1812 69.7092 26.9417 70 27.6944C70.2932 28.4534 70.1441 29.173 70.3611 29.9444C72.4066 37.2174 72.9759 44.4699 73.625 52C73.9619 55.9082 74.1987 59.7174 74.8611 63.5972C75.3576 66.5054 75.1883 69.3425 75.2639 72.2917C75.3604 76.0551 75.909 79.7718 76.375 83.5C76.7815 86.7518 76.5 90.1659 76.5 93.4444C76.5 98.223 75.6301 104.417 71.3056 107.25C69.1246 108.679 65.5421 109.468 62.9444 109.5C59.3211 109.544 55.7202 109.522 52.1111 109.25C49.6857 109.067 47.4724 109.089 45.5972 107.458C43.591 105.714 43.4528 102.578 42.5 100.264C41.5475 97.9506 41.6873 95.5539 41.1111 93.1667C40.8002 91.8784 40.3134 90.7537 40 89.5M109.25 40.75C107.356 41.0943 104.629 43.5009 103.556 45C101.356 48.0728 99.1504 52.0771 98.0417 55.6806C97.1271 58.6528 97 61.9176 97 65C97 69.751 96.7191 74.4539 97.7222 79.1111C98.1355 81.03 98.1141 83.0148 98.5 84.9444C98.7698 86.2934 99.4318 87.5271 100.014 88.7639C100.838 90.5157 101.849 92.1882 103.194 93.5833C104.563 95.003 105.853 94.75 107.861 94.75C110.53 94.75 112.928 95.6268 115.194 93.75C117.847 91.5536 119.651 88.3755 120.75 85.1389C121.377 83.2937 121.743 81.4897 122.167 79.5972C122.689 77.2645 123.736 75.1626 124.111 72.7778C125.179 65.9802 125.36 58.2916 124.111 51.5139C123.342 47.3401 120.502 45.4889 117.611 42.75C115.46 40.712 113.324 40.75 110.5 40.75M143.5 15C143.5 16.3485 143.731 17.6492 143.75 19C143.768 20.2482 144.096 21.4528 144.194 22.6944C144.306 24.1089 144.169 25.5324 144.306 26.9444C144.556 29.5405 145 32.0206 145 34.6389V44.1111C145 50.9559 145.442 57.7282 145.5 64.5556C145.524 67.3955 146.013 70.3294 146.48 73.1328L146.5 73.25C146.924 75.7967 146.669 78.3735 147.056 80.8889C147.863 86.1373 147 91.6297 147 96.8889C147 97.3908 147.204 96.2717 147.25 96.1389C147.712 94.801 148.356 93.5622 148.75 92.1944C149.685 88.9466 150.703 86.0684 152.444 83.1667C153.382 81.6042 154.422 80.1921 155 78.4444C155.515 76.886 156.032 75.6191 156.944 74.25C158.757 71.5308 159.304 68.1586 161.097 65.4306C162.357 63.5129 162.927 61.1593 164.375 59.3889C166.03 57.3662 167.322 55.0648 169.111 53.1528C170.919 51.2205 172.468 48.8488 175 47.7639C176.82 46.9839 179.342 46.2393 181.361 46.7778C183.816 47.4323 185.701 50.239 186.861 52.25C189.269 56.4235 191.727 61.3183 191.986 66.2361C192.139 69.1451 192.5 71.9523 192.5 74.8889V84.5556C192.5 88.5374 193.25 92.5224 193.25 96.5C193.25 97.5415 193.5 98.5581 193.5 99.6111C193.5 101.027 194.003 100.144 194.5 99.25M213 35C213.988 35.1098 214.44 39.2519 214.556 40.1667C215.088 44.371 216.4 48.3885 217.111 52.5278C217.575 55.2243 217.624 57.9182 218.25 60.5972C219.022 63.8987 219.698 67.0932 219.75 70.5C219.833 75.9121 221.5 81.2139 221.5 86.5972C221.5 89.3439 222.25 91.9867 222.25 94.7222C222.25 95.6613 222.476 98.8138 222.5 95.8889C222.528 92.5498 223.382 89.4448 224.056 86.1944C224.968 81.7887 225.466 77.1218 227.014 72.8889C229.212 66.8757 231.583 60.9636 233.75 54.9444C235.058 51.3121 237.361 48.0133 239.5 44.8056C241.273 42.1458 243.683 40.4873 245.972 38.3889C247.703 36.8027 248.965 36.6412 251.25 37C254.815 37.5598 259.348 38.8616 260.125 42.9167C261.139 48.2101 261 53.4973 261 58.8611C261 64.2454 261.25 69.6135 261.25 75V88.5C261.25 91.4691 261.721 94.8651 261 97.75M22 218.75C22 216.974 22.4022 215.325 22.5972 213.569C22.9328 210.549 22.6977 207.476 22.75 204.444C22.8097 200.982 23.4752 197.53 23.625 194.056C23.7713 190.662 23.75 187.271 23.75 183.875V153.139C23.75 150.125 24 147.13 24 144.125C24 140.748 23.5 137.423 23.5 134.056V130.861C23.5 130.629 23.3692 129.047 23.4028 129.069C23.7269 129.285 23.953 129.918 24.1111 130.222C24.876 131.696 25.6748 133.146 26.5556 134.556C28.5239 137.705 30.8112 140.656 32.8333 143.778C35.7763 148.321 38.5089 152.853 41.1667 157.556C42.2018 159.387 43.5619 160.784 44.375 162.764C45.1516 164.655 45.5263 166.821 46.5139 168.611C47.6945 170.751 48.0517 173.156 49.3056 175.306C50.4925 177.34 51.7753 179.658 52.6111 181.861C53.0649 183.057 53.4528 185.045 54.25 186.042C54.3897 186.216 54.4666 185.65 54.5556 185.444C54.8912 184.67 55.2308 183.893 55.6111 183.139C56.8552 180.674 58.2746 178.312 59.4444 175.806C61.369 171.681 63.2084 167.502 65.0972 163.375C66.7428 159.779 69.0111 156.394 70.5 152.75C73.0352 146.545 75.4225 140.196 78.3333 134.181C79.0427 132.714 79.5944 131.179 80.4444 129.806C80.941 129.003 81.2129 127.757 82 127.167C82.1809 127.031 82.2987 128.315 82.3056 128.361C82.5034 129.697 82.415 131.067 82.7639 132.375C83.623 135.597 83.3658 138.925 83.8889 142.194C84.3748 145.232 83.8302 148.42 84.125 151.486C84.4353 154.713 84.25 158.009 84.25 161.25C84.25 171.604 84.5519 182.048 86.25 192.236C87.0964 197.315 87.7275 202.409 88.4444 207.5C88.7462 209.643 89 211.836 89 214C89 214.598 88.9545 216.977 89.5 217.25M148.5 217.25C148.5 216.076 148.234 214.922 148.25 213.75C148.279 211.589 148.724 209.331 148.875 207.153C149.277 201.367 149.5 195.679 149.5 189.875C149.5 185.238 148.479 180.603 147.861 176.014C147.28 171.696 146.217 167.379 145.361 163.097C144.803 160.308 143.424 158.014 140.639 157.194C137.524 156.278 133.442 156.083 130.306 156.944C124.436 158.556 120.51 163.734 117.5 168.639C113.339 175.418 110.317 182.584 109.25 190.5C108.979 192.51 108.727 194.469 108.75 196.5C108.774 198.597 109.505 200.55 110.139 202.528C111.151 205.687 112.206 210.478 115.5 212.125C116.541 212.645 117.528 212.509 118.694 212.5C121.071 212.482 123.781 212.258 125.736 210.722C127.567 209.283 129.294 207.812 131.194 206.444C133.618 204.701 135.068 202.118 136.625 199.639C138.474 196.695 140.373 193.847 141.806 190.694C142.649 188.84 143.725 187.229 144.361 185.264C144.741 184.089 145.055 182.821 145.556 181.694C146.098 180.475 147.173 179.387 147.25 178C147.299 177.113 147.118 176.263 147.5 175.5M170.75 116.25C169.786 116.491 170.25 123.711 170.25 124.736C170.25 129.375 171.238 133.991 171.944 138.556C172.359 141.232 172.524 143.942 172.889 146.625C173.324 149.829 174.01 153.005 174.347 156.222C174.663 159.242 174.342 162.284 174.611 165.306C174.901 168.557 175.144 171.739 175.306 175C175.601 180.955 175.145 186.985 175.75 192.917C176.047 195.824 176.5 198.638 176.5 201.556V212.583C176.5 213.263 176.083 216.417 176.75 216.75M233.25 215.25C233.553 211.921 234.149 208.625 234.361 205.25C234.81 198.091 234.611 190.97 234.764 183.806C234.86 179.275 235.305 174.968 234.25 170.514C233.81 168.656 233.308 166.775 232.236 165.167C231.296 163.756 230.036 162.587 229 161.25C227.637 159.491 226.008 157.775 223.694 157.556C219.846 157.191 216.531 158.799 213.486 161.014C209.094 164.208 205.618 168.491 202.694 173.056C199.821 177.542 198.399 182.493 196.875 187.528C195.308 192.705 194.637 198.561 194.514 203.958C194.456 206.488 193.822 210.212 195.75 212.25C197.098 213.675 197.804 214.244 199.806 214.444C203.657 214.83 206.901 212.943 210 210.861C213.567 208.465 215.974 205.311 218.611 202C219.823 200.478 220.978 198.947 222.25 197.472C224.021 195.418 224.742 193.011 226 190.667C228.206 186.557 231.277 183.093 232.972 178.667C233.527 177.218 233.874 175.505 234.25 174M257 161.5C257.227 164.457 258.446 167.21 258.944 170.111C259.602 173.936 260.101 177.839 260.514 181.694C261.42 190.149 262.497 198.528 263.847 206.931C264.099 208.499 263.744 210.175 264.194 211.694C264.257 211.906 264.25 212.349 264.25 211.75C264.25 210.014 264.341 208.27 264.514 206.542C264.954 202.14 266.122 197.618 267.444 193.417C268.401 190.376 268.8 187.18 269.625 184.097C270.574 180.55 270.737 177.114 272.111 173.653C273.106 171.146 273.25 168.358 274.25 165.917C275.211 163.57 276.261 160.185 279.194 160.25C280.999 160.29 283.323 162.135 284.722 163.139C287.144 164.876 287.752 168.056 288.5 170.75C290.313 177.277 290.5 184.019 290.5 190.75V200.278M290.5 200.278V200.625C290.5 201.19 290.499 200.303 290.5 200.278ZM290.5 200.278C290.56 198.062 291.143 195.939 291.694 193.806C292.91 189.106 294.699 184.36 295.236 179.528C295.903 173.522 298.557 167.994 300.986 162.528C302.307 159.556 305.674 159.5 308.722 159.5C312.087 159.5 312.883 163.993 313.25 166.5C314.11 172.379 313.75 178.622 313.75 184.556C313.75 186.936 314 189.288 314 191.667C314 194.276 313.883 196.906 314.194 199.5C314.68 203.544 315.991 207.477 317.5 211.25"
                    stroke="black" strokeWidth="9" strokeLinecap="round" />
                </svg>
              </Link>


              <div className="relative group mr-8 mt-6 min-[768px]:hidden">
                <button onClick={toggleDropdown} onMouseEnter={showDropdown} className="flex items-center pl-4 py-2 text-sm text-gray-700 rounded-md focus:outline-none focus:ring-0 focus:ring-offset-0 focus:ring-transparent">
                  <img className="w-8" src={`${process.env.PUBLIC_URL}/menuIcon.svg`} alt="" />
                </button>
                {isDropdownOpen && (
                  <div onMouseLeave={hideDropdown} className="absolute hidden group-hover:block z-10 w-52 py-1 mt-0 bg-white border border-gray-200 rounded-md right-0">
                    <a href="/#biography"><div className='flex flex-row items-center font-opensans mx-4 my-4'>Biography</div></a>
                    <Link to="/books"><div className='flex flex-row items-center font-opensans mx-4 my-4'>Books</div></Link>
                    <Link to="/malamfamilyhistory"><div className='flex flex-row items-center font-opensans mx-4 my-4'>Malam Family History</div></Link>
                    <Link to="/malamfamilyhistory"><div className='flex flex-row items-center font-opensans mx-4 my-4 pl-6'>&bull; Origins</div></Link>
                    <Link to="/familytree"><div className='flex flex-row items-center font-opensans mx-4 my-4 pl-6'>&bull; Family Tree</div></Link>
                    <Link to="/edwardmalam"><div className='flex flex-row items-center font-opensans mx-4 my-4 pl-6'>&bull; Edward Malam</div></Link>
                    <Link to="/contact"><div className='flex flex-row items-center font-opensans mx-4 my-4'>Contact</div></Link>
                  </div>
                )}
              </div>
            </div>

            <div className='edward-article flex flex-col font-opensans text-left max-[768px]:m-6 min-[768px]:m-10 max-w-[800px]'>






              <nav className="edward-part-nav" aria-label="Edward Malam sections">
                {edwardSections.map((section) => (
                  <Link
                    key={section.id}
                    to={section.to}
                    className={currentEdwardPart === section.part ? 'active' : ''}
                    aria-current={currentEdwardPart === section.part ? 'page' : undefined}
                  >
                    {section.label}
                  </Link>
                ))}
              </nav>

              {currentEdwardPart === 'early-life' && (
                <>
                  <div className='edward-overview-layout'>
                    <div className="edward-overview-copy">
                      <p className='mb-4'>Edward Malam was the son of a Tunstall cloth-seller, who went into business with his mother, running drapers'
                        shops in Tunstall and Stoke. When this business closed, Edward set up on his own account, establishing the
                        Hanley
                        Brewery and owning a large number of public houses, most of which were in North Staffordshire: it was a very
                        successful business.</p>
                      <p className='mb-4'>Edward married late in life, and died in 1909, aged 80. He bequeathed his considerable estate to be divided
                        between his sister, Edna, and his home town of Tunstall, for the benefit of its residents. His widow, Clarissa,
                        who was to receive
                        a small annuity, contested Edward's will, which was upheld by the Probate Court, where the details of their
                        unhappy marriage became public knowledge.</p>
                      <p className='mb-4'>Edward's sizeable bequest to Tunstall funded the building of the Edward Malam Convalescent Home at Deganwy,
                        North
                        Wales. Today, more than 100 years after his death, individuals and organisations in Stoke-on-Trent and the
                        Staffordshire Moorlands can apply to <a target="_blank" rel="noreferrer" className='underline'
                          href="https://staffordshire.foundation/grants/malamfundforindividuals/">The Malam Fund</a> for grants to
                        support
                        residents of these districts.</p>
                    </div>

                    <aside className="edward-bio-box" aria-label="Edward Malam biography summary">
                      <figure className="edward-bio-portrait">
                        <img src={`${process.env.PUBLIC_URL}/edwardmalampics/edwardmalamportrait.png`} className=''
                          alt="Edward Malam portrait"
                          onClick={() => openLightbox(`${process.env.PUBLIC_URL}/edwardmalampics/edwardmalamportrait.png`)}
                        />
                        <figcaption>Edward Malam, c.1890 (photograph published with Edward's
                          obituary, 1909)</figcaption>
                      </figure>
                    </aside>
                  </div>

                  <div className="edward-fact-table-card">
                    <table className="edward-fact-table">
                      <tbody>
                        <tr>
                          <th scope="row">Date and place of birth:</th>
                          <td>about 1829; Tunstall, North Staffordshire</td>
                        </tr>
                        <tr>
                          <th scope="row">Date and place of baptism:</th>
                          <td>2 October 1831; St Margaret's Church, Wolstanton</td>
                        </tr>
                        <tr>
                          <th scope="row">Date and place of marriage:</th>
                          <td>24 December 1884; Holy Trinity Church, Hartshill</td>
                        </tr>
                        <tr>
                          <th scope="row">Spouse:</th>
                          <td>Clarissa Bird (née Finney)</td>
                        </tr>
                        <tr>
                          <th scope="row">Issue:</th>
                          <td>0</td>
                        </tr>
                        <tr>
                          <th scope="row">Residence:</th>
                          <td>"The Cliffe", Cliffe Bank, Stoke-on-Trent and "Mayfield", The Brampton, Newcastle-under-Lyme</td>
                        </tr>
                        <tr>
                          <th scope="row">Date of death and age:</th>
                          <td>26 September 1909; aged 80</td>
                        </tr>
                        <tr>
                          <th scope="row">Date and place of burial:</th>
                          <td>30 September 1909; Hanley Cemetery, Shelton, Stoke-on-Trent ST4 2DL</td>
                        </tr>
                        <tr>
                          <th scope="row">Relationship to myself (JM):</th>
                          <td>Second cousin, three times removed</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <h2 id="born-into-business-family" className='font-bold text-2xl mt-4 mb-2'>Born into a business family</h2>
                  <p className='mb-4'>Edward Malam was born c.1829, in the town of Tunstall, one of six small towns in North Staffordshire known as
                    “The Potteries”, due to them being the centre of ceramic production.</p>

                  <p className='mb-4'>He was the son of Thomas Malam (1807&mdash;1832) and Emma Malam née Taylor (c.1812&mdash;1880), and was
                    baptised
                    in 1831 at the age of two alongside his younger sister, Edna (1831&mdash;1902),
                    at St Margaret's Church, Wolstanton. His mother, Emma, was aged 15 when she married Thomas, 19: she was to have
                    a
                    profound influence on his life.</p>

                  <p className='mb-4'>Edward's father, Thomas, was in business in Tunstall as a mercer (a merchant who traded in fine cloth, cotton,
                    linen, and woollen fabrics). Known details of Thomas's life are:</p>


                  <ul className='list-disc pl-5 space-y-4'>
                    <li>
                      <p className='mb-4'><b>1827:</b> Thomas Malam was a subscriber to the building of a new church at Tunstall, with a donation of
                        two guineas (£2 2s). (Staffordshire Advertiser, 01 December 1827)</p>
                    </li>

                    <p className='mb-4'><i>Christ Church, High Street, Tunstall, was built 1831&mdash;32; £1,000 of the total cost was raised by
                      private subscription.</i></p>

                    <li>
                      <p className='mb-4'><b>1827:</b> Thomas Malam's house at Tunstall was broken into by William Lees, who stole property to the
                        value of £30, including a piece of cloth which was recovered by the police; Lees was found guilty and
                        sentenced to be transported for seven years. (Staffordshire Advertiser, 22 March 1828)</p>
                    </li>

                    <li>
                      <p className='mb-4'><b>1831:</b> Thomas Malam was one of 60 local businessmen who signed a declaration to form the Burslem and
                        Wolstanton Anti-Truck Association, which opposed the Truck System. (Staffordshire Advertiser, 17 November
                        1831)</p>
                    </li>

                    <p className='mb-4'><i>Under the Truck System, employers paid their workers in tokens, not in coin of the realm. These
                      company-issued tokens could only be exchanged at the company store, where goods were sold at inflated
                      prices.
                      The Truck Act 1831 outlawed this practice.</i></p>

                    <li>
                      <p className='mb-4'><b>1832:</b> Thomas Malam was one of 300 residents of Tunstall who signed a petition opposed to the setting
                        up of trades unions in the district. (Staffordshire Advertiser, 30 June 1832) </p>
                    </li>

                    <li>
                      <p className='mb-4'><b>15 November 1832:</b> Thomas Malam made his will and died the same day, aged 25, leaving an estate
                        valued
                        at under £600. In his will, he left his mercer's business at Tunstall to his wife Emma, for her benefit and
                        for the benefit of their children “for their maintenance and education in the world”. (Staffordshire,
                        Dioceses
                        of Lichfield and Coventry Wills and Probate 1521&mdash;1860)</p>
                    </li>
                  </ul>


                  <div className="edward-text-figure-section">
                    <h2 id="twice-a-widow" className='font-bold text-2xl mt-4 mb-2'>Twice a widow</h2>
                    <figure className="edward-inline-figure edward-inline-figure-right">
                      <img src={`${process.env.PUBLIC_URL}/edwardmalampics/staffordshireadvertiser.png`} alt="Staffordshire Advertiser notice for Webb and Malam goods" />
                      <figcaption>Notices appeared in the local press advertising the goods
                        sold by Webb and Malam, this one appearing in the Staffordshire Advertiser in November 1860</figcaption>
                    </figure>
                    <p className='mb-4'>In 1834, two years after her husband's death, Mrs Emma Malam, widow, aged 22, married John Webb
                      (c.1811&mdash;1839), a mercer and draper with businesses in Market Place and High Street, Tunstall. Thereafter
                      she
                      was Mrs Emma Webb &mdash; the name she used for the rest of her life. </p>
                    <p className='mb-4'>Tragedy struck Emma again, when she was widowed for the second time. Her husband, John Webb, died in 1839, aged
                      28. John died without leaving a will, and Emma was granted the administration of his estate, valued at under
                      £450.
                    </p>

                    <p className='mb-4'>Twice widowed, and still aged only 27, and with two young children from her first marriage, Edward, 10, and
                      Edna,
                      8, Emma Webb was nothing but determined to do her very best for herself and her family, no doubt mindful of her
                      first husband's bequest that his estate be for their maintenance and education.</p>
                  </div>


                  <div>
                    <h2 id="mother-and-son-partnership" className='font-bold text-2xl mt-4 mb-2'>Mother and son in partnership</h2>
                    <p className='mb-4'>Both of Emma Webb's deceased husbands had been in business in the textile trade and, not surprisingly, it was
                      this business into which she entered, in partnership with her son, Edward Malam. </p>

                    <p className='mb-4'>The partnership, known as Webb and Malam, mercers and woollen drapers, had a small chain of draper's shops in
                      The
                      Potteries, with the “flagship” shops being at 47 and 48 High Street, Stoke, and branches in Tunstall and
                      Burslem:
                    </p>

                    <ul className='list-disc pl-5 space-y-4'>
                      <li>47 High Street, Stoke-on-Trent (late Michael Briggs, draper's; opened 1854 as a general draper's shop) </li>
                      <li>48 High Street, Stoke-on-Trent (late Charles Doody, draper's; opened May 1855 as a gentleman's outfitters)
                      </li>
                      <li>Market Place, Tunstall, adjoining the Sneyd Arms Hotel (opened 1856)</li>
                      <li>1 St John's Square, Burslem (opened 1866)</li>
                      <li>Market Place, Burslem (date uncertain)</li>
                    </ul>

                    <br />

                    <p className='mb-4'>Emma appears to have been the partnership's buyer, making trips to “the principal London and Provincial Firms”
                      to
                      select the latest fashions for their shops, such as ready-made mantles, silks, shawls, shirts, dresses, furs,
                      bonnets, gloves and parasols.</p>

                    <p className='mb-4'>Emma also selected cloth, the names of which are now seldom used. There was droguet (a French term for a ribbed
                      woollen dress fabric), and linsey (a coarse linen). These, and other fabrics, including tweed, were fashioned
                      into
                      ready-made clothes for the workers in The Potteries &mdash; such as jackets, dresses, trousers, coats, shirts
                      and
                      vests. </p>

                    <p className='mb-4'>Edward's role in the partnership is less certain but, with his mother away on frequent buying trips, it's not
                      unreasonable to speculate that he looked after the day-to-day running of the shops, and kept the business
                      accounts.</p>

                  </div>

                  <div>
                    <h2 id="expanding-business" className='font-bold text-2xl mt-4 mb-2'>Expanding business</h2>
                    <p className='mb-4'>A fire in 1854 could have put Emma and Edward out of business. Started by a gas explosion in the cellar of the
                      47
                      High Street, Stoke, premises, it took two hours to be extinguished with the aid of two fire engines. Extensive
                      damage to the stock was caused by smoke and water. (Staffordshire Advertiser, 02 December 1854)</p>

                    <p className='mb-4'>Undeterred, Emma and Edward purchased the salvaged goods, valued at “upwards of £1,500” from the insurers and
                      sold it off at a “tremendous sacrifice” in a one-day sale, to make way for “an entirely new stock”.
                      (Staffordshire
                      Advertiser, 09 December 1854) </p>

                    <p className='mb-4'>A glimpse inside the Stoke showroom comes from an 1855 advertisement, which invites the area's ladies to visit
                      the Silk Department, where they will be shown “exceedingly rich and tasteful designs in brocades, plaids, and
                      striped silks and poplins”. In the Shawl and Mantle Room they will see new styles and shapes, the Ladies' Dress
                      Department and the Bonnet and Millinery Rooms are stocked with the season's latest fashions, and the Family
                      Linen
                      and Drapery Departments offer goods from leading manufacturers, including Welsh and Lancashire flannels and
                      carpets from Kidderminster and Brussels weavers. (Staffordshire Advertiser, 21 April 1855) </p>

                    <div className='max-[768px]:hidden float-right w-1/2 bg-white p-4 border-2 border-[#ccc] rounded-xl ml-4 mb-4'>
                      <img src={`${process.env.PUBLIC_URL}/edwardmalampics/eurekashirt.png`} />
                      <p className='mb-4'>Advertisement for Richard Ford's "Eureka Shirts", 1851,
                        which were sold by Webb and Malam</p>
                    </div>

                    <div className='flex flex-col items-center min-[768px]:hidden float-right w-full bg-white p-4 border-2 border-[#ccc] rounded-xl mx-auto mb-4'>
                      <img src={`${process.env.PUBLIC_URL}/edwardmalampics/eurekashirt.png`} className='w-full' />
                      <p className='mt-4'>Advertisement for Richard Ford's "Eureka Shirts", 1851,
                        which were sold by Webb and Malam</p>
                    </div>

                    <p className='mb-4'>An advertisement from 1856 refers to “talented cutters employed” (Staffordshire Advertiser, 19 April 1856), and
                      one from 1859 draws attention to them employing a “cutter of first-class ability, whose experience in London and
                      other large towns enables them to guarantee the perfect fit of all orders entrusted to them. (Staffordshire
                      Advertiser, 14 May 1859)</p>

                    <p className='mb-4'>Throughout the 1850s, the business advertised for apprentices, junior assistants, porters, milliners, and
                      saleswomen.</p>

                    <p className='mb-4'>Webb and Malam became agents for what today would be referred to as “branded goods”. There were Richard Ford's
                      "Eureka Shirts" (six shirts for 33 shillings), made in London and bearing the maker's label, and Simmons and
                      Woodro2
                      hats, made in Oldham. (Staffordshire Advertiser, 19 April 1856)</p>

                    <p className='mb-4'>In addition to buying the businesses (premises and stock) of other local drapers (Michael Briggs, Stoke, 1854;
                      Charles Doody, Stoke, 1855; Knight Brothers, Burslem, 1866), and reopening the shops as Webb and Malam
                      establishments, Emma and Edward also bought up stock which they liquidated at discounted prices, no doubt
                      helping
                      the cash-flow of the business. </p>

                    <p className='mb-4'>For example, in 1859, Webb and Malam purchased the stock of William Malkin, a deceased draper of Tunstall, and
                      sold it off “at such desperate prices as must effect an immediate clearance”. (Staffordshire Advertiser, 03
                      December 1859)</p>
                  </div>

                  <h2 id="thieves-at-work" className='font-bold text-2xl mt-4 mb-2'>Thieves at work </h2>
                  <p className='mb-4'>Theft was an ever-present problem, and the local press records numerous instances of goods being stolen from
                    the
                    Webb and Malam shops. If found guilty, a convicted thief was given a sentence which, for the time, was
                    considered
                    entirely fitting to their crime.</p>

                  <div className="bg-[#ebeaea] p-6 rounded-lg mb-8">
                    <p className='mb-2'><b>Name:</b> Jane Mayer (alias “Mary Davies”)</p>
                    <p className='mb-2'><b>Age:</b> 23</p>
                    <p className='mb-2'><b>Date:</b> 1861</p>
                    <p className='mb-2'><b>Charged with:</b> Stealing 36 yards of woollen plaid cloth
                      from Webb and Malam, Tunstall, on 15 April 1861;
                      the following day she pledged a portion of the cloth at Hulme's pawnbrokers, Burslem (pawning stolen goods was
                      how
                      petty thieves converted their ill-gotten gains into ready cash)</p>
                    <p className='mb-2'><b>Verdict:</b> Not Guilty</p>
                    <p className='mb-2'>The cloth remained at the pawnbrokers for more than a year, until 9 May 1862, when Mayer returned to claim it
                      &mdash; and was arrested. </p>
                    <p className='mb-2'>She had been unable to recover the cloth sooner, due to the fact she was in Knutsford Gaol, serving nine
                      months
                      for theft from a draper at Congleton; at her arrest she gave the false name “Mary Davies”.</p>
                    <p className='mb-2'>At her trial, in which she defended herself, Mayer denied stealing the cloth from Webb and Malam. To account
                      for
                      how she came by the pawnbroker's ticket for the cloth, she claimed to have bought it for 1 shilling from a
                      woman
                      named Jones, who had since moved to live in North Wales. </p>
                    <p className=''>The jury was convinced by Mayer's defence, and she was found not guilty. (Staffordshire Advertiser, 17 May &
                      05
                      July 1862) </p>

                  </div>

                  <div className="bg-[#ebeaea] p-6 rounded-lg mb-8">
                    <p className='mb-2'><b>Name:</b> John Walton and John Fairbrother</p>
                    <p className='mb-2'><b>Age:</b> Described as “boys”</p>
                    <p className='mb-2'><b>Date:</b> 1862</p>
                    <p className='mb-2'><b>Charged with:</b> Stealing a pair of trousers from Webb and
                      Malam, Tunstall </p>
                    <p className='mb-2'><b>Verdict:</b> Guilty</p>
                    <p className='mb-2'><b>Sentence:</b> One lash of the whip and two months in prison
                    </p>
                    <p className=''>(Staffordshire Advertiser, 22 March 1862) </p>
                  </div>

                  <div className="bg-[#ebeaea] p-6 rounded-lg mb-8">
                    <p className='mb-2'><b>Name:</b> James McGuire, George and Mary Ann Wilkinson </p>
                    <p className='mb-2'><b>Date:</b> 1862</p>
                    <p className='mb-2'><b>Charged with:</b> McGuire was charged with stealing a jacket
                      from Webb and Malam, Tunstall, which he pawned
                      for 9 shillings; he shared the money with his accomplices, George and Mary Ann Wilkinson</p>
                    <p className='mb-2'><b>Verdict:</b> Guilty</p>
                    <p className='mb-2'><b>Sentence:</b> Three months' hard labour for each of them</p>
                    <p className=''>(Staffordshire Advertiser, 26 July 1862)</p>
                  </div>

                  <div className="bg-[#ebeaea] p-6 rounded-lg mb-8">
                    <p className='mb-2'><b>Name:</b> John Smith</p>
                    <p className='mb-2'><b>Date:</b> 1868</p>
                    <p className='mb-2'><b>Charged with:</b> Stealing 91 yards of calico cloth from
                      Webb and Malam, Burslem</p>
                    <p className='mb-2'><b>Verdict:</b> Guilty</p>
                    <p className='mb-2'><b>Sentence:</b> Four months' in prison</p>
                    <p className=''>(Staffordshire Advertiser, 25 July 1868)</p>
                  </div>

                  {/* <div className='max-[768px]:hidden float-right w-1/2 bg-white p-4 border-2 border-[#ccc] rounded-xl ml-4 mb-4'>
            <img src={`${process.env.PUBLIC_URL}/edwardmalampics/selinamoss.jpg`} />
            <p className='mb-2'>Police photograph of Selina Moss, a shop-lifter found
              guilty of theft from Webb and Malam's Burslem shop in 1869.</p>
            <br />
            <p className='mb-2'>Her hands are raised to show an identifying feature of one
              or both hands, which was customary in 19th century police photographs. Did she deliberately move her left hand
              as the photograph was being taken, causing the image to be blurred and the distinctive feature to be concealed
              from view?</p>
            <br />
            <p className=''>&copy; Staffordshire & Stoke on Trent Archive Service</p>
          </div> */}

                  {/* <div>
            <img src="${process.env.PUBLIC_URL}/edwardmalampics/selinamoss.jpg" />
            <p className='mb-4'>Police photograph of Selina Moss, a shop-lifter found
              guilty of theft from Webb and Malam's Burslem shop in 1869.</p>
            <br />
            <p className='mb-4'>Her hands are raised to show an identifying feature of one
              or both hands, which was customary in 19th century police photographs. Did she deliberately move her left hand
              as the photograph was being taken, causing the image to be blurred and the distinctive feature to be concealed
              from view?</p>
            <br />
            <p className='mb-4'>&copy; Staffordshire & Stoke on Trent Archive Service</p>
          </div> */}

                  <div className="selina-moss-card bg-[#ebeaea] p-6 rounded-lg mb-8">
                    {/* <div className='flex flex-row max-[768px]:hidden float-right w-1/3 bg-white p-4 border-2 border-[#ccc] rounded-xl ml-4 mb-4'>
              <img src={`${process.env.PUBLIC_URL}/edwardmalampics/selinamoss.jpg`} className='object-contain' />
              <p className='mb-2'>Police photograph of Selina Moss, a shop-lifter found
                guilty of theft from Webb and Malam's Burslem shop in 1869.</p>
              <br />
              <p className='mb-2'>Her hands are raised to show an identifying feature of one
                or both hands, which was customary in 19th century police photographs. Did she deliberately move her left hand
                as the photograph was being taken, causing the image to be blurred and the distinctive feature to be concealed
                from view?</p>
              <br />
                <p className=''>&copy; Staffordshire & Stoke on Trent Archive Service</p>
            </div> */}

                    <div className="selina-moss-details">
                      <p className='mb-2'><b>Name:</b> Selina Moss</p>
                      <p className='mb-2'><b>Age:</b> 38</p>
                      <p className='mb-2'><b>Occupation:</b> None</p>
                      <p className='mb-2'><b>Date:</b> 1869</p>
                      <p className='mb-2'><b>Charged with:</b> An “old offender”, charged with stealing a
                        pair of boy's trousers valued at 4 shillings
                        and 7 pence from Webb and Malam, Burslem, and 36 yards of alpaca cloth from Richard Benbow, draper, Burslem
                      </p>
                      <p className='mb-2'><b>Verdict:</b> Guilty</p>
                      <p className='mb-2'><b>Sentence:</b> Seven years in prison (she was released early
                        as in 1874 she was again sentenced to time in jail for a futher offence)</p>
                      <p className='mb-6'>(Staffordshire Advertiser, 10 & 24 July 1869)</p>
                      <p className=''>Selina's other convictions include stealing boots etc, October 1862 (6 months jail); drunk, May 1874 (1 month
                        jail); drunk, April 1878 (7 days jail); stealing a watch, April 1881 (1 year jail); non payment of rate, March
                        1883 (7 days jail); stealing 3 pounds' weight of potatoes, June 1884</p>
                    </div>

                    <figure className="selina-moss-figure">
                      <img
                        src={`${process.env.PUBLIC_URL}/edwardmalampics/selinamoss.jpg`}
                        alt="Police photograph of Selina Moss"
                      />
                      <figcaption>
                        <p>Police photograph of Selina Moss, a shop-lifter found
                          guilty of theft from Webb and Malam's Burslem shop in 1869.</p>
                        <p>Her hands are raised to show an identifying feature of one
                          or both hands, which was customary in 19th century police photographs. Did she deliberately move her left hand
                          as the photograph was being taken, causing the image to be blurred and the distinctive feature to be concealed
                          from view?</p>
                        <p className="selina-moss-credit">&copy; Staffordshire & Stoke on Trent Archive Service</p>
                      </figcaption>
                    </figure>
                  </div>




                  <h2 id="partnership-ends" className='font-bold text-2xl mt-4 mb-2'>The partnership ends</h2>

                  <p className='mb-4'>In February 1866, a notice appeared in the local press informing residents that the business of Webb and Malam,
                    47 and 48 High Street, Stoke, had been sold, and the proprietors were retiring. Stock to the value of £4,783 10s
                    was to be disposed of in a sale. (Staffordshire Advertiser, 03 March 1866)</p>

                  <p className='mb-4'>The shops in Tunstall and Burslem continued to trade in the name of Webb and Malam for the next three years
                    until, on the 29 September 1869, the partnership between Emma Webb, aged 57, and her son, Edward Malam, aged 40,
                    was dissolved by mutual consent. </p>

                  <p className='mb-4'>From then on, Emma was to carry on the business of draper and woollen merchant under her own account
                    (Staffordshire Advertiser, 24 December 1869), but Edward was about to begin in an altogether different line of
                    business, in which his entrepreneurship flourished to the full.</p>

                  <div>
                    <img src={`${process.env.PUBLIC_URL}/edwardmalampics/Staffordshire_Advertiser_-_Friday_24_December_1869.png`} />
                    <p className='mb-4'>Notice of the partnership between Emma Webb and Edward
                      Malam (mother and son) ending on 29th September 1869 (Staffordshire Advertiser, 24th December 1869)</p>
                  </div>


                </>
              )}

              {currentEdwardPart === 'brewery' && (
                <>


                  <div
                  >
                    <img src={`${process.env.PUBLIC_URL}/edwardmalampics/HANLEY_BREWERY_LOCATION.png`}
                    />
                    <p className='mb-4'>Hanley Brewery location, at the end of Boundary Street,
                      Vale Place, Hanley</p>
                    <br />
                    <p className='mb-4'>OS 25-inch survey, 1898. Reproduced with the permission of
                      the National Library of Scotland</p>
                  </div>

                  <h2 id="edward-malam-brewer" className='font-bold text-2xl mt-4 mb-2'>Edward Malam, brewer</h2>

                  <p className='mb-4'>Edward's new business venture was in the brewery industry. In 1868, the Hanley Brewery, Vale Place, Hanley, was
                    sold by auction. Described as an “old-established brewery” (Staffordshire Advertiser, 23 May 1868), it was
                    bought
                    by Edward and his business partner, George Butler, trading under the name Malam and Butler.</p>

                  <p className='mb-4'>An indication of the size and layout of the brewery comes from the 1868 sale particulars, which describe it as
                    standing on 767 square yards of land, with stables, yards, outbuildings, and three houses fronting the main road
                    from Hanley to Burslem. Fitted with “all the recent improvements … capable of turning out 200 barrels per week …
                    and well adapted for a lucrative business”. (Staffordshire Advertiser, 25 July 1868)</p>

                  <p className='mb-4'>It was a short-lived partnership, perhaps for only a year or two, which was dissolved in 1870, after which
                    Edward
                    was the sole proprietor. (Staffordshire Advertiser, 02 April 1870)</p>


                  <div>
                    <img src={`${process.env.PUBLIC_URL}/edwardmalampics/E.MALAM-LETTERHEAD.jpg`} />
                    <p className='mb-4'>Hanley Brewery account sheet, c.1892, recording
                      outstanding balance owed by the Red Lion Hotel, Leek, for purchase of beer</p>
                  </div>

                  <h2 id="thriving-business" className='font-bold text-2xl mt-4 mb-2'>A thriving business</h2>
                  <p className='mb-4'>For the next 40 years, Edward developed the Hanley Brewery into a thriving and successful business. His
                    letterhead described him as “Brewer of Ale, Porter, and East India Pale Ale”.</p>
                  <p className='mb-4'>A news item, published several years after his death, gives an insight not only to his business style, but also
                    his character.</p>
                  <p className='mb-4'>Described as being a “well-known figure in the Potteries” he had “old-fashioned habits. He managed his business
                    personally … collected his own accounts, and steadily refused to convert a flourishing and growing business into
                    a
                    limited liability company.” (Birmingham Daily Post, 31 July 1915)</p>
                  <p className='mb-4'>Key to Edward's success was in buying up local beerhouses, which the Hanley Brewery &mdash; his brewery &mdash;
                    sold beer to.
                    These pubs were “tied houses”, where the publicans were tenants of the Hanley Brewery, renting the pubs and
                    buying
                    their beer from the brewery.</p>
                  <p className='mb-4'>An insight to Hanley Brewery's beers comes from the annual Brewers' Exhibition held at the Agricultural Hall,
                    Islington, London, in October 1890:</p>
                  <p className='mb-4'><i>Amongst the mild beers a splendid 22lb beer brewed by Mr E. Malam of the Hanley Brewery, Staffordshire, with
                    17
                    per cent flaked malt, was perhaps the pick. This was a very full drinking beer, carrying a beautiful head and
                    very
                    pale in colour. The same gentlemen also sent a magnificent 27lb beer containing 15 per cent flaked malt, which
                    was
                    particularly admirable. (The Brewers' Guardian, vol.20, 1890, p.364)</i></p>
                  <p className='mb-4'>Over the course of his long ownership of the Hanley Brewery, Edward owned many pubs throughout The Potteries
                    and
                    North Staffordshire: his obituary (1909) refers to him owning 50 pubs (see box for list known Hanley Brewery
                    pubs).</p>


                  <div className="infoBox">

                    <h3><b>Hanley Brewery public houses owned by Edward Malam</b></h3>
                    <br />
                    <table>
                      <tr>
                        <th>Place</th>
                        <th>Sign of House</th>
                      </tr>
                      <tr>
                        <td>Alsagers Bank</td>
                        <td><b>Farmer's Boy</b></td>
                      </tr>
                      <tr>
                        <td>Audley</td>
                        <td>The Anchor</td>
                      </tr>
                      <tr>
                        <td>Audley</td>
                        <td><b>Boughey Arms</b></td>
                      </tr>
                      <tr>
                        <td>Audley</td>
                        <td>Wheatsheaf Inn</td>
                      </tr>
                      <tr>
                        <td>Bagnall</td>
                        <td><b>Stafford Arms</b></td>
                      </tr>
                      <tr>
                        <td>Blythe Bridge</td>
                        <td><b>Duke of Wellington</b></td>
                      </tr>
                      <tr>
                        <td>Bradley Green</td>
                        <td><b>Bird in Hand</b></td>
                      </tr>
                      <tr>
                        <td>Bucknall</td>
                        <td><b>Spring Cottage</b></td>
                      </tr>
                      <tr>
                        <td>Burslem</td>
                        <td>Bricklayer's Arms</td>
                      </tr>
                      <tr>
                        <td>Burslem</td>
                        <td>Crewe Arms</td>
                      </tr>
                      <tr>
                        <td>Burslem</td>
                        <td>Mitre Inn</td>
                      </tr>
                      <tr>
                        <td>Chesterton</td>
                        <td>Crown Inn</td>
                      </tr>
                      <tr>
                        <td>Chesterton</td>
                        <td>Grove Inn</td>
                      </tr>
                      <tr>
                        <td>Chesterton</td>
                        <td><b>Malt Shovel</b></td>
                      </tr>
                      <tr>
                        <td>Chesterton</td>
                        <td><b>Roebuck</b></td>
                      </tr>
                      <tr>
                        <td>Chesterton</td>
                        <td>Royal Oak</td>
                      </tr>
                      <tr>
                        <td>Chesterton</td>
                        <td>Wheatsheaf</td>
                      </tr>
                      <tr>
                        <td>Chesterton</td>
                        <td>Yew Tree Inn</td>
                      </tr>
                      <tr>
                        <td>Cobridge</td>
                        <td>Black Boy</td>
                      </tr>
                      <tr>
                        <td>Fenton</td>
                        <td>High Park Inn</td>
                      </tr>
                      <tr>
                        <td>Goldenhill</td>
                        <td><b>Red Lion</b></td>
                      </tr>
                      <tr>
                        <td>Hanley</td>
                        <td>Artillery Gun</td>
                      </tr>
                      <tr>
                        <td>Hanley</td>
                        <td>The Beehive</td>
                      </tr>
                      <tr>
                        <td>Hanley</td>
                        <td>Bricklayer's Arms</td>
                      </tr>
                      <tr>
                        <td>Hanley</td>
                        <td>The Cock</td>
                      </tr>
                      <tr>
                        <td>Hanley</td>
                        <td>Cross Keys</td>
                      </tr>
                      <tr>
                        <td>Hanley</td>
                        <td>Hope & Anchor</td>
                      </tr>
                      <tr>
                        <td>Hanley</td>
                        <td>Horse & Jockey</td>
                      </tr>
                      <tr>
                        <td>Longton</td>
                        <td>The Fountain</td>
                      </tr>
                      <tr>
                        <td>Milton</td>
                        <td><b>The Abbey</b></td>
                      </tr>
                      <tr>
                        <td>Nantwich</td>
                        <td>Nantwich Arms</td>
                      </tr>
                      <tr>
                        <td>Newcastle</td>
                        <td>Fountain Inn</td>
                      </tr>
                      <tr>
                        <td>Newcastle</td>
                        <td><b>Crown & Anchor</b></td>
                      </tr>
                      <tr>
                        <td>Newcastle</td>
                        <td>Plough Inn</td>
                      </tr>
                      <tr>
                        <td>Newcastle</td>
                        <td>Red Bull</td>
                      </tr>
                      <tr>
                        <td>Penkhull</td>
                        <td>Marquis of Granby</td>
                      </tr>
                      <tr>
                        <td>Silverdale</td>
                        <td>George & Dragon</td>
                      </tr>
                      <tr>
                        <td>Silverdale</td>
                        <td>Foresters Arms</td>
                      </tr>
                      <tr>
                        <td>Silverdale</td>
                        <td><b>Roebuck</b></td>
                      </tr>
                      <tr>
                        <td>Tunstall</td>
                        <td>Market Tavern</td>
                      </tr>
                      <tr>
                        <td>Werrington</td>
                        <td><b>Mill Inn</b></td>
                      </tr>
                      <tr>
                        <td>Wolstanton</td>
                        <td>Malt Shovel</td>
                      </tr>
                      <tr>
                        <td>Wolstanton</td>
                        <td>Oddfellows Arms</td>
                      </tr>
                    </table>
                    <br />
                    <p className='mb-4'><b>Name in bold</b> shows 14 pubs which Messrs Charrington & Co. expressed an interest in buying in 1906 from
                      Edward Malam (source: Letter from Bill & Son, Hotel & Brewery Valuers, Birmingham to Edward Malam's solicitor,
                      Thomas Robinson, Leek).</p>

                  </div>


                </>
              )}

              {currentEdwardPart === 'family' && (
                <>

                  {/* <!-- <div >
            <img src="${process.env.PUBLIC_URL}/edwardmalampics/RELATIONSHIP CHART copy.png"  />
          </div> --> */}

                  <EdwardImmediateFamilyTree />

                  <h2 id="marries-mothers-nurse" className='font-bold text-2xl mt-4 mb-2'>Marries his mother's nurse</h2>

                  <p className='mb-4'>Edward's mother, and former partner in the family draper's business, Emma Webb, died in March 1880, aged 68.
                    She
                    left a personal estate valued at £196 15s 6d. Emma was buried in Hanley Cemetery.</p>
                  <p className='mb-4'>Edward, his younger sister Edna, and their mother lived together at ""The Cliffe"", Cliffe Bank,
                    Stoke-on-Trent.
                    Census records show that the family had a live-in domestic servant, one of whom was to marry into the Malam
                    family
                    &ndash; with unfortunate consequences for herself.</p>
                  <p className='mb-4'>Our first encounter with Clarissa Bird comes in the 1881 census, where she is recorded as an unmarried
                    23-year-old, born in London, and living with Edward and Edna Malam as their domestic servant. A later account
                    tells how she had joined the household to nurse Edward's mother, Emma. (Staffordshire Sentinel, 17 March 1911)
                  </p>
                  <p className='mb-4'>Clarissa was retained as a domestic servant following Emma's death in 1880, and in 1884 she married her
                    employer,
                    Edward Malam. It was Clarissa's second marriage, and Edward's first.</p>



                  <h2 id="clarissa-first-marriage" className='font-bold text-2xl mt-4 mb-2'>Clarissa Bird &mdash; her first marriage</h2>

                  <p className='mb-4'>Born in Bethnal Green, London, in 1856, Clarissa Bird had moved to Congleton, Cheshire, as a child. She married
                    Samuel Finney of Stoke in 1875, when she was aged 18. Within a few months, the marriage was in trouble and
                    Samuel
                    Finney was in court charged with assaulting his wife of five months.</p>

                  <p className='mb-4'>The court heard how Samuel had thrown Clarissa out of their Gloucester Street, Hanley, house three times. In
                    the
                    most recent altercation, he had struck her when she had asked for the return of 5 shillings which he had taken
                    from her pocket. Clarissa retaliated by throwing a chair at him; Samuel hit her again, and left the room,
                    slamming
                    the door behind him. Clarissa then threw a pottery match-holder at a mirror, breaking both.</p>

                  <p className='mb-4'>The magistrates considered there were faults on both sides, and "advised the demonstrative pair not to give way
                    to temper". Clarissa was advised "to try to live comfortably with her husband". Samuel Finney was bound over in
                    the sum of £20 and ordered to keep the peace for six months. (Staffordshire Sentinel, 30 December 1875)</p>

                  <p className='mb-4'>The relationship didn't improve. In August 1876 Samuel Finney placed a public notice in the local newspaper:
                  </p>


                  <div>
                    <img src={`${process.env.PUBLIC_URL}/edwardmalampics/Samuel_Finney_1876__copy.png`} />
                    <p className='mb-4'>Samuel Finney giving notice in August 1876 that he will
                      not be responsible for any debts incurred by his wife, Clarissa</p>
                  </div>

                  <blockquote>
                    <p className='mb-4'><i>I, Samuel Finney, Tobacconist, No.39 Bethesda Street, Hanley, hereby give notice that I will not be
                      responsible for any debt or debts my wife, Clarissa Finney, may contract after this date.</i></p>
                    <p className='mb-4'>August 27, 1876</p>
                    <p className='mb-4'>(Staffordshire Sentinel, 29 August 1876)</p>
                  </blockquote>

                  <p className='mb-4'>Clarissa and Samuel Finney were divorced in 1882, by which time she had reverted to her maiden name, Bird, and
                    had moved into the Malam household &ndash; at first as nurse to Emma Webb, and then, after Emma's death, as a
                    domestic servant for her children, Edward and Edna Malam.</p>


                  <h2 id="unhappy-marriage" className='font-bold text-2xl mt-4 mb-2'>Edward Malam's unhappy marriage</h2>

                  <p className='mb-4'>Edward Malam and Clarissa Bird were married on Christmas Eve 1884, at Holy Trinity Church, Hartshill. Edward
                    was
                    55, a self-made and successful businessman, owner of the Hanley Brewery and a growing number of pubs; Clarissa
                    was
                    28, divorced, and a domestic servant.</p>

                  <div>
                    <img src={`${process.env.PUBLIC_URL}/edwardmalampics/Edward_Malam_Clarissa_Bird_marriage_1884.png`}
                    />
                    <p className='mb-4'>Marriage entry of Edward Malam and Clarissa Bird, 24th
                      December 1884, at Holy Trinity Church, Hartshill, Stoke-on-Trent</p>
                  </div>

                  <p className='mb-4'>Details of Edward and Clarissa's marriage became public knowledge in a dispute brought by Clarissa over
                    Edward's
                    will. It was reported in detail in the local newspaper:</p>

                  <blockquote>
                    <p className='mb-4'><i>The married life was an unhappy one. Unfortunately [Mrs Malam] gave way to drink and on occasions was
                      guilty
                      of assault on her husband.</i></p>

                    <p className='mb-4'><i>In October 1896, Mrs Malam [informed] Mr Robinson of Messrs Challinors and Shaw, solicitors, Leek ...
                      [and]
                      said she desired to have a separation from her husband and she desired Mr Robinson to see her husband in
                      reference to it ... [Mr Malam] said he would be only too willing and that he would allow his wife £2 a week.
                      He then said he desired to make a will, and Mr Robinson took his instructions for it.</i></p>

                    <p className='mb-4'>(Staffordshire Sentinel, 17 March 1911)</p>

                  </blockquote>

                  <p className='mb-4'>In the same news report, George Mole, Edward's coachman, said they "did not live happily together", that Mrs
                    Malam's habits as to drink were "very bad", and that when under the influence of drink "she had thrown [Mr
                    Malam's] boots many a time into the yard". She also complained about him coming home late from work.</p>



                  <h2 id="edward-and-edna-rift" className='font-bold text-2xl mt-4 mb-2'>Edward and Edna Malam — a rift between them?</h2>


                  <p className='mb-4'>Incomplete correspondence held at Staffordshire Record Office (7374/14) between Edward and Edna Malam hints at
                    a
                    rift between brother and sister.</p>

                  <p className='mb-4'>On 11 November 1882, Edna's solicitors wrote to Edward's solicitors, regarding an "agreement for partition" of
                    the family property. Through her solicitors, Edna said she wished to avoid the expense of a valuation, and that:
                  </p>

                  <blockquote>
                    <p className='mb-4'><i>She is led to believe that the Brewery is of very great value and if taken alone will largely exceed the
                      value
                      of the rest of the property which as you know consists of "The Cliffe" and some cottages at Burslem (the
                      latter
                      being as we understand in a terrible state of dilapidation).</i></p>
                  </blockquote>

                  <p className='mb-4'>Edna made the following proposal:</p>

                  <blockquote>
                    <p className='mb-4'><i>Miss Malam considers that if Mr Malam takes the Brewery and the cottages adjoining and she "The Cliffe"
                      and
                      the
                      Burslem cottages he ought to pay her in addition £2,000.</i></p>
                  </blockquote>

                  <p className='mb-4'>Edward's reply to his solicitors came five days later, on 16 November:</p>

                  <blockquote>
                    <p className='mb-4'><i>The proposals of my sister are simply absurd.</i></p>
                  </blockquote>

                  <p className='mb-4'>The next letter to survive (30 April 1883) shows the strain that existed between Edward and Edna, as he writes
                    to
                    his solicitor saying:</p>

                  <blockquote>
                    <p className='mb-4'><i>Not having had any communications with my sister at all since I last saw you after which upon considering
                      the
                      damnable lease they wanted me to sign to lease my own property, I decided that any valuation to be made for
                      sale
                      of her share and purchase by me of my late mother's portion of the Brewery, would be the only reference I
                      could
                      agree to, a portion of it being now in a terrible tumbledown state, and cannot possibly go more than another
                      year
                      or so without rebuilding &ndash; which I should to carry on my business be compelled to do...</i></p>
                  </blockquote>

                  <p className='mb-4'>Later that year, in August 1883, there was an exchange of letters regarding Edward's rent book, which his
                    sister
                    had taken. This was clearly an important book, containing not only rents owed to Edward, but rents owed to his
                    late mother, and to his late uncle, both of which he continued to collect.</p>

                  <p className='mb-4'>Edna appears to have returned a portion cut from the rent book, which Edward said was:</p>

                  <blockquote>
                    <p className='mb-4'><i>... of little use to me without the book itself ... the loss and inconvenience to me of my book is serious
                      and
                      also its mutilation as it is also causing me to be kept out of my own rents. Have I no means of enforcing
                      its
                      return? Have they a right to force their way into the house ["The Cliffe"] in my absence and take away my
                      own
                      property? Without the book I can make out no accounts.</i></p>
                  </blockquote>

                  <p className='mb-4'>Edward also accuses his sister of telling an "untruth" when she said that her solicitor had the rent book, and
                    that she was being advised by an excise officer "in conjunction with the woman Crompton". This is clearly a
                    reference to Annie Crompton, a family servant who had entered the Malam household by 1881, when she is recorded
                    in
                    the census.</p>

                  <p className='mb-4'>Edna Malam and Annie Crompton had evidently formed a close friendship, as by the 1891 census, they were living
                    together, 65 miles north-west from The Potteries, on the Lancashire coast at Birkdale, near Southport. Annie
                    Crompton is described as Edna's "ladies companion".</p>

                  <p className='mb-4'>Annie Crompton was originally from Wigan, about 20 miles from Southport, and it may have been her Lancashire
                    connection that encouraged Edna to make the move to Birkdale &ndash; an area with which she had no previous
                    connection. At any rate, the move took Edna away from Edward, their falling-out over the family's considerable
                    property interests, and his marriage to the family servant, Clarissa Bird. Perhaps Edna hoped the move to
                    Birkdale
                    would give her the chance of a new start, and an independent life away from the family business.</p>





                  {/* <!-- <div style="background-color: #ffffff; margin: 0px 0 10px 10px; padding: 10px; width: 200px; float: right; border-radius: 8px; border: 2px solid #e5e5e5;">
            <img src="${process.env.PUBLIC_URL}/edwardmalampics/RELATIONSHIP CHART copy.png" style="width: 180px;float: right; border-radius: 8px; margin-bottom: 10px;" />
            <p className='mb-4'>Hanley Brewery account sheet, c.1892, recording outstanding balance owed by the Red Lion Hotel, Leek, for purchase of beer</p>
          </div> --> */}

                  <div className="edward-map-pair">
                    <figure>
                      <img
                        src={`${process.env.PUBLIC_URL}/edwardmalampics/THE_CLIFFE_LOCATION.png`}
                        alt='Map showing location of “The Cliffe”, Cliffe Bank, Stoke-on-Trent'
                      />
                      <figcaption>Location of “The Cliffe”, Cliffe Bank, Stoke-on-Trent,
                        home of the Malam family 1871&mdash;1905</figcaption>
                    </figure>

                    <figure>
                      <img
                        src={`${process.env.PUBLIC_URL}/edwardmalampics/MAYFIELD_LOCATION_copy.png`}
                        alt='Map showing location of “Mayfield”, The Brampton, Newcastle-under-Lyme'
                      />
                      <figcaption>Location of “Mayfield”, The Brampton,
                        Newcastle-under-Lyme, home of Edward Malam c.1906/07&mdash;1909</figcaption>
                    </figure>

                    <p>Both images OS 25-inch survey, 1898. Reproduced with the
                      permission of the National Library of Scotland</p>
                  </div>

                  <h2 id="cliffe-and-mayfield" className='font-bold text-2xl mt-4 mb-2'>"The Cliffe" and "Mayfield" &mdash; Edward Malam's homes</h2>



                  <p className='mb-4'>Census records show that for more than 30 years, from 1871 until 1901, the Malam family lived at "The Cliffe",
                    Cliffe Bank, Stoke-on-Trent. It was evidently a well-known property, as it was sufficient for Edward to give his
                    address simply as ""The Cliffe", Stoke-on-Trent" in a handwritten letter (1901), and his mother's grave (1880)
                    records that she was of ""The Cliffe", Stoke-on-Trent".</p>

                  <p className='mb-4'>Electoral registers show that Edward was still resident at "The Cliffe" in 1905, but by 1907 he was living at
                    "Mayfield", The Brampton, Newcastle-under-Lyme. This exclusive road was home to successful businessmen in the
                    ceramics, furniture, railway, and iron trades, living in substantial Victorian villas, each in its own grounds
                    and
                    set back from the road.</p>

                  <p className='mb-4'>Perhaps named after the nearby placename of May Bank, "Mayfield" was built c.1853 for Thomas Edwards, a
                    cabinet-maker from Newcastle.</p>

                  <p className='mb-4'>It seems highly probable that Edward Malam intended to retire from the brewery business and enjoy a comfortable
                    retirement at "Mayfield," as Messrs Charrington & Co. had expressed an interest in buying 14 pubs from him in
                    1906
                    (see list of Hanley Brewery pubs, above).</p>

                  <p className='mb-4'>However, Edward's ownership of "Mayfield" was to be brief &ndash; perhaps no more
                    than two years.</p>


                </>
              )}

              {currentEdwardPart === 'will' && (
                <>

                  <div>
                    <img src={`${process.env.PUBLIC_URL}/edwardmalampics/EDWARD_MALAM_GRAVE_copy.jpg`} />
                    <p className='mb-4'>Grave of Emma Webb (c.1812&mdash;80) and her son Edward
                      Malam (c.1829&mdash;1909), Hanley Cemetery, Stoke-on-Trent</p>
                  </div>

                  <h2 id="death-and-will" className='font-bold text-2xl mt-4 mb-2'>Edward Malam's death and will</h2>

                  <p className='mb-4'>Edward Malam died on 26 September 1909, aged 80. He was buried in the family vault at Hanley Cemetery, where
                    his
                    mother had been laid to rest in 1880.</p>

                  <p className='mb-4'>An obituary was published in the Staffordshire Sentinel, 1 October 1909, with a portrait photograph of Edward
                    &ndash; the only known image of him. He was described as of "Mayfield, Newcastle, proprietor of the Hanley
                    Brewery".</p>

                  <p className='mb-4'>Clarissa Malam, Edward's widow, is not listed as one of the funeral mourners, although she did send a wreath.
                  </p>

                  <p className='mb-4'>Shortly after Edward's death, reports began to appear in the local newspaper that he had bequeathed between
                    £30,000 and £40,000 to Tunstall, his home town, for the express purpose of building a convalescent home for the
                    benefit of the town's inhabitants. The bequest was to the local authority, Tunstall Urban District Council.</p>

                  <p className='mb-4'>In late October 1909, a notice was published by Edward's executor, Thomas Robinson of Challinors and Shaw,
                    solicitors, Leek, that "all claims and demands upon this estate be sent in forthwith to us".</p>

                  <p className='mb-4'>There was a claim &ndash; from Clarissa Malam, Edward's widow, who contested his will.</p>


                  <div>
                    <img src={`${process.env.PUBLIC_URL}/edwardmalampics/will_dispute.png`} />
                    <p className='mb-4'>Newspaper report of Edward Malam's bequest and his widow,
                      Clarissa disputing his will (Staffordshire Sentinel, 17th March 1911)</p>
                  </div>

                  <h2 id="will-disputed" className='font-bold text-2xl mt-4 mb-2'>Will disputed by Edward's widow: a "painful story"</h2>

                  <p className='mb-4'>The case of Malam v. Robinson was heard in the Probate Court in March 1911. It was, as the newspaper headline
                    put
                    it, a "painful story" as details of their unhappy marriage became known.</p>

                  <p className='mb-4'>For much of their 25 years of marriage, Edward and Clarissa had lived independently of each other, but,
                    importantly, they still lived at the same address ("The Cliffe", Cliffe Bank, Stoke-on-Trent until c.1907, and
                    then
                    at "Mayfield", The Brampton, Newcastle-under-Lyme). They remained husband and wife.</p>

                  <p className='mb-4'>If Clarissa had expected to be a major beneficiary of Edward's will, she was to be disappointed. The details of
                    Edward's estate and will were:</p>


                  <ul>
                    <li>Estate valued at £53,610 (approximately £6.5m in today's terms)</li>
                    <li>Three-quarters of the estate (about £35,000) in trust for the building of a convalescent home "for my native
                      town of Tunstall to be called by my name"</li>
                    <li>One-quarter of the estate to his sister, Edna Malam</li>
                    <li>An annuity of £100 (£2 per week) to his wife, Clarissa Malam</li>
                  </ul>

                  <br />

                  <p className='mb-4'>Clarissa challenged the will on the grounds that the document, dated 4 November 1896, had been drawn up when
                    the
                    couple had contemplated a separation, and that it was only to be effective if they had indeed separated. The
                    fact
                    they had continued to live together at the same address was evidence that no such separation had taken place,
                    claimed Clarissa, and therefore the will was not a valid document.</p>


                  <p className='mb-4'>In court, Clarissa was asked:</p>

                  <blockquote>
                    <p className='mb-4'><i>“Did he at any time tell you that he had made a will?”</i></p>

                    <p className='mb-4'><i>“No,”</i> she replied.</p>

                    <p className='mb-4'><i>“Did he at any time tell you that he was going to make a will?”</i></p>

                    <p className='mb-4'><i>“He did towards the last,”</i> said Clarissa. <i>“He told me that he wanted everything left to me for life
                      and asked me if I would get somebody to draw up a will. I said I would get someone to do so when he was
                      better
                      and that he was not well enough then to sign one.”</i></p>
                  </blockquote>


                  <div>
                    <img src={`${process.env.PUBLIC_URL}/edwardmalampics/Edward_Malam_will_1896.png`} />
                    <p className='mb-4'>Edward Malam's will made 4th November 1896, which was
                      disputed by Clarissa, his widow</p>
                  </div>


                  <h2 id="judges-decision" className='font-bold text-2xl mt-4 mb-2'>The judge's decision</h2>

                  <p className='mb-4'>After hearing the evidence from both sides, the judge said it was evident that Edward Malam had given clear
                    instructions and that at the time (1896) he had intended the resulting document to be his will. The judge
                    pronounced in favour of the will, and ordered that the costs of the case be paid out of the estate.
                  </p>


                  <h2 id="clarissa-afterward" className='font-bold text-2xl mt-4 mb-2'>Clarissa &mdash; what happened to her?</h2>

                  <p className='mb-4'>At the time of the Probate Court case in 1911, Clarissa was living on her own at 8 Alma Grove, Basford, Stoke,
                    supported by "private means". The 1921 census records her living alone at 4 Chamberlain Avenue, Stoke. She died
                    in
                    1928, aged 71, and was buried, not with her husband in Hanley Cemetery, but at Stoke Cemetery (often referred to
                    locally as Hartshill Cemetery).
                  </p>


                  <div>
                    <img src={`${process.env.PUBLIC_URL}/edwardmalampics/Staffordshire_Advertiser_-_Saturday_31_July_1915.png`} />
                    <p className='mb-4'>Newspapers across the Midlands and the North-West of
                      England reported John Henry Malam's good fortune as heir of Edward Malam (Staffordshire Advertiser 31st July
                      1915)</p>
                  </div>

                  <h2 id="search-for-heir" className='font-bold text-2xl mt-4 mb-2'>Search for an heir of Edward Malam</h2>

                  <p className='mb-4'>Edward had left one-quarter of his estate to his sister, Edna Malam. But, as Edna had predeceased Edward by
                    seven
                    years (she died in 1902, unmarried, and without children), the search began to find Edward's closest living heir
                    who would be entitled to receive Edna's share of Edward's estate.</p>

                  <p className='mb-4'>A notice appeared in several newspapers in August 1913, calling for persons claiming to be heirs-at-law of
                    Edward
                    Malam to come forward. A footnote gave Edward's pedigree, noting he was the son of Thomas Malam of Weston, near
                    Crewe, and the grandson of Thomas Malam of Wybunbury, near Crewe. (Staffordshire Advertiser, 23 August 1913)</p>

                  <p className='mb-4'>After checking church records and tombstones at Wybunbury, John Henry Malam (1866&mdash;1915), a farm labourer
                    with eight children, living at 109 Wistaston Road, Crewe, and working on a farm at Weston, was identified as
                    Edward Malam's heir. John was Edward's second cousin once removed: as distant relatives, it's likely they were
                    unknown to one another.</p>

                  <p className='mb-4'>John Henry Malam was officially pronounced Edward Malam's heir-at-law on 12 December 1914, by the Master in
                    Chancery (an official of the Court of Chancery, where the proof of his claim was examined and upheld).</p>

                  <p className='mb-4'>It was an unexpected inheritance for John, but he didn't live long enough to enjoy it. He died just five months
                    after hearing the news, in May 1915, aged 49. John had made a will, leaving £9,304 4s to his widow, Ellen, and
                    their children (approximately £793,000 in today's terms).</p>

                  <p className='mb-4'>When details of John's will became known, newspapers ran a "rags to riches" story about a farm labourer who had
                    come into a fortune: "Many people wondered how a man in such humble circumstances had become possessed of so
                    large
                    a fortune," said one. (Birmingham Daily Post, 31 July 1915)</p>


                </>
              )}

              {currentEdwardPart === 'legacy' && (
                <>

                  <h2 id="convalescent-home" className='font-bold text-2xl mt-4 mb-2'>The Edward Malam Convalescent Home</h2>

                  <div>
                    <img src={`${process.env.PUBLIC_URL}/edwardmalampics/MALAM_HOME_&_CONWAY_CASTLE_copy.jpg`} />
                    <p className='mb-4'>Postcard view of the Edward Malam Convalescent Home,
                      c.1920, showing its tranquil setting at Conwy, North Wales &mdash; the distant mountains, Conwy Castle and
                      River Conwy a world away from the streets and factories of the North Staffordshire Potteries</p>
                  </div>

                  <div className="infoBox">

                    <p className='mb-4'><b>Address:</b> Warren Drive, Deganwy, LL31 9ST</p>
                    <p className='mb-4'><b>Grid Reference:</b> SH7873278443</p>
                    <p className='mb-4'><b>CADW Primary Record Number:</b> 62303</p>

                    <p className='mb-4'><b>Built:</b> 1913</p>
                    <p className='mb-4'><b>Cost:</b> £9,000</p>
                    <p className='mb-4'><b>Opened:</b> 1914</p>
                    <p className='mb-4'><b>Closed:</b> 1966</p>

                    <p className='mb-4'><b>Present-day name:</b> Malam Court</p>
                    <p className='mb-4'><b>Opened:</b> 1969</p>

                  </div>

                  <p className='mb-4'>The bulk of Edward Malam's estate was left to Tunstall Urban District Council, for the building of a
                    convalescent
                    home in his name, to be used for the benefit of residents of Tunstall, who were to stay there for convalescent
                    and
                    recuperative holidays.</p>


                  <p className='mb-4'>Land was purchased at Deganwy, near Conwy, North Wales, and a substantial building was designed by architects
                    from Tunstall and Hanley, built by Sampson Salt, builders of Leek, and fitted out in oak by Jones, Moss & Co of
                    Newcastle-under-Lyme. Standing on a hill, it had two wings &ndash; one for men, one for women &ndash; and was
                    set
                    within its own grounds, where there was a kitchen garden, and lawns which could be used for tennis and bowling.
                    The cost was £9,000. (Staffordshire Sentinel, 23 June 1954)</p>


                  <p className='mb-4'>A bronze plaque in the entrance hall declared:</p>

                  <blockquote>
                    <p className='mb-4'><i>This Home was erected and endowed by Edward Malam Esq., of Newcastle-under-Lyme, brewer, for the benefit
                      of
                      his native town of Tunstall, Staffordshire. A.D. 1913.</i></p>
                  </blockquote>

                  <div>
                    <img src={`${process.env.PUBLIC_URL}/edwardmalampics/Staffordshire_Sentinel_-_Wednesday_06_May_1914.png`} />
                    <p className='mb-4'>Newspaper headline for the opening of the Edward Malam
                      Convalescent Home (Staffordshire Sentinel, 6th May 1914)</p>
                  </div>

                  <p className='mb-4'>Miss Graham, superintendent nurse at Chell Workhouse Hospital, Tunstall, was appointed as matron, on a salary
                    of
                    £70pa, with residence and rations. She was joined by an assistant matron (£30pa), a female cook (£25pa), and two
                    housemaids (£14pa each) &ndash; all recruited from The Potteries. The Staffordshire Sentinel described these
                    appointments as creating a "local atmosphere", with the Sentinel keeping both patients and staff in touch with
                    news from home. (Staffordshire Sentinel, 26 May 1915)</p>

                  <p className='mb-4'>The Home was formally opened on Tuesday, 5 May 1914, and the first patients arrived on 15 May. It could
                    accommodate 14 men and 12 women, and must have seemed a world away from The Potteries, with stunning views
                    across
                    to Conwy harbour, Conwy Castle, and the Conwy Mountains in the distance.</p>


                  <h2 id="world-war-one-hospital" className='font-bold text-2xl mt-4 mb-2'>World War I Auxiliary Hospital</h2>

                  <p className='mb-4'>The respite offered by the Edward Malam Convalescent Home to the residents of Tunstall was interrupted on 4
                    August 1914, when Britain declared war on Germany. By May 1915 the home had been taken over by the military
                    authorities for use as an auxiliary hospital for the care of injured soldiers. The first group of 23 soldiers
                    arrived on 22 May 1915. (Staffordshire Sentinel, 26 May 1915)</p>

                  <p className='mb-4'>Soldiers were still there in July 1918, when a sale of work at the Home included items made by them. (North
                    Wales
                    Weekly News, 11 July 1918)</p>


                  <h2 id="after-the-war" className='font-bold text-2xl mt-4 mb-2'>After the war</h2>

                  <p className='mb-4'>With the ending of the war in 1918, the Home returned to providing for the residents of Tunstall. It opened
                    late
                    April / early May each year, closing for the winter months. Patients were charged a maintenance fee as follows:
                  </p>

                  <ul>
                    <li>1921 &ndash; £1 11s 6d (three-week stay)</li>
                    <li>1933 &ndash; £1 1s (three-week stay)</li>
                    <li>1940 &ndash; £1 5s (three-week stay)</li>
                    <li>1948 &ndash; £3 (two-week stay)</li>
                    <li>1952 &ndash; £2 12s 6d (per week)</li>
                    <li>1955 &ndash; £7 (two-week stay)</li>
                    <li>1961 &ndash; £7 (two-week stay)</li>
                  </ul>

                  <br />

                  <p className='mb-4'>Each year, the Home was inspected by the Management Committee who travelled by train from Tunstall, and
                    newspaper
                    articles give glowing reports and patient testimonials of its high standard.</p>


                  <h2 id="world-war-two-evacuees" className='font-bold text-2xl mt-4 mb-2'>World War II evacuees</h2>

                  <p className='mb-4'>During World War II, the Home again found itself used as a safe haven, though not this time for injured
                    soldiers.
                    Instead, from 1941 it was used by Liverpool Education Committee for 40 children with disabilities, evacuated
                    from
                    the city (it was known as the Edward Malam Home Residential Council School for Evacuated Physically Defective
                    Children). Nurses from Liverpool relocated to Deganwy with the children. (Liverpool Daily Post, 14 March 1944)
                  </p>

                  <p className='mb-4'>The evacuees stayed at the Home until July 1946, when they returned to Liverpool following a farewell from the
                    Mayor and Mayoress of Conwy, who presented each child with a silver coin. (North Wales Weekly News, 25 July
                    1946)
                  </p>


                  <div>
                    <img src={`${process.env.PUBLIC_URL}/edwardmalampics/EDWARD_MALAM_FUND_FAIR_copy.png`} />
                    <p className='mb-4'>"Tunstall's Big Day" says this advertisement from the
                      Staffordshire Sentinel for a Grand Carnival in aid of the Edward Malam Convalescent Home, held in Tunstall
                      Park, 1 June 1957</p>
                  </div>

                  <h2 id="appeals-for-funds" className='font-bold text-2xl mt-4 mb-2'>Appeals for funds</h2>

                  <p className='mb-4'>Britain was in a period of austerity following World War II, and the effects were felt by the Home. In 1954,
                    the
                    Trustees approached Stoke-on-Trent council for a grant, to enable them to keep the Home open. Investments from
                    Edward Malam's original bequest were given as £30,441, and the Home's annual income, which was fixed, was £738.
                    Since the Home had reopened in 1948, it had made annual losses. The council approved the request, with funds to
                    be
                    made available at the end of the financial year. (Staffordshire Sentinel, 23 June 1954)</p>

                  <p className='mb-4'>1955 saw a significant change for the Home, when its terms of use as instructed by Edward Malam were amended to
                    include all residents in Stoke-on-Trent, not just those living in Tunstall. Moreover, under the new terms it was
                    to be for the sole use of women, with men being treated at the Heath Home for Men, Llanfairfechan.
                    (Staffordshire
                    Sentinel, 23 June 1954)</p>

                  <p className='mb-4'>A gift of £55 was received in 1955, the proceeds of the winding up of the City of Stoke-on-Trent Fat Stock
                    Society, which had ceased to exist in 1934. (Staffordshire Sentinel, 18 August 1955)</p>

                  <p className='mb-4'>The following year, the Stoke-on-Trent (North) Constituency Labour Party organised a fair in Tunstall Park, to
                    raise funds for the Home, which was in need of refurbishment to bring it up to modern standards. The costs of
                    maintaining the Home were rising and putting pressure on the trust fund set up for it. A crowd of 10,000
                    attended
                    and £825 was raised (Staffordshire Sentinel, 30 May 1956 & 29 May 1957) </p>

                  <p className='mb-4'>A second fair was held in 1957, again organised by the local Labour Party: 12,000 attended and £1,000 was
                    raised.
                    (Staffordshire Sentinel, 18 July 1957)</p>


                  <div>
                    <img src={`${process.env.PUBLIC_URL}/edwardmalampics/MALAM_COURT_copy.jpg`} />
                    <p className='mb-4'>The Edward Malam Convalescent Home was sold and converted
                      into private apartments in the late 1960s, and is known today as Malam Court</p>
                  </div>

                  <h2 id="home-is-sold" className='font-bold text-2xl mt-4 mb-2'>The Home is sold</h2>

                  <p className='mb-4'>The Edward Malam Convalescent Home was, in fact, one of three similar homes in North Wales owned and operated
                    by
                    the city of Stoke-on-Trent. As times changed and costs mounted, it was inevitable that this should come under
                    scrutiny, and in November 1965 a notice from the Charity Commission appeared in the Staffordshire Sentinel for a
                    scheme to administer the charity and confer the power to sell the Home. (Staffordshire Sentinel, 12 November
                    1965)
                  </p>

                  <p className='mb-4'>By September 1966, the Home was being advertised for sale at £4,200, or higher offers. Its grounds were
                    advertised separately, with planning permission for three detached houses.</p>

                  <p className='mb-4'>In June 1969, it was announced that the Home, renamed Malam Court, had been converted into nine luxury one- and
                    two-bedroom apartments, each with central heating and its own garage. (North Wales Weekly News, 26 June 1969) By
                    July 1970, seven had been sold, and only two remained.</p>


                  <div>
                    <img src={`${process.env.PUBLIC_URL}/edwardmalampics/CONVALESCENT_FUND_copy.jpg`}
                    />
                  </div>

                  <h2 id="malam-fund" className='font-bold text-2xl mt-4 mb-2'>The Malam Fund &mdash; for the people of Stoke</h2>

                  <p className='mb-4'>What of Edward Malam's legacy today? More than 100 years have passed since his death, and his bequest is still
                    helping the people of Stoke.</p>

                  <p className='mb-4'>Today, the Edward Malam Convalescent Fund, originally set up as a charitable trust in 1913, is merged with two
                    other former trusts: The Heath Memorial Trust and The John Pepper Fund. The remit of all three were broadly
                    similar, but applied to different areas of Stoke-on-Trent. By merging the three together, it has created one
                    fund
                    under the name "The Malam Fund", that can be used across the whole city, administered by the Community
                    Foundation
                    for Staffordshire.</p>

                  <p className='mb-4'>The Malam Fund awards grants to organizations who provide social activities for older people, or those who are
                    suffering from social isolation. It also offers grants to individuals who need a holiday or a break away, for
                    the
                    purposes of respite, convalescence, to recover from a recent traumatic event or where there would be a benefit
                    to
                    the physical or mental health of an applicant.</p>

                  <p className='mb-4'>The Malam Fund is open to applicants based or working in the areas of Stoke-on-Trent or the Staffordshire
                    Moorlands. For more information about The Malam Fund, <a target="_blank" rel="noreferrer"
                      href="https://staffordshire.foundation/grants/malamfundforindividuals/">click here</a>.</p>



                  <div
                  >
                    <img src={`${process.env.PUBLIC_URL}/edwardmalampics/MALAM_STREET_SIGN_copy.jpg`}
                    />
                    <p className='mb-4'>Malam Street, Hanley, close to where Hanley Brewery stood,
                      was named for Edward Malam in 1953. John Malam, author of this article, who is Edward Malam's second cousin,
                      three times removed, is shown standing next to the street sign (February 2023)</p>
                  </div>

                  <h2 id="hanley-brewery" className='font-bold text-2xl mt-4 mb-2'>What happened to the Hanley Brewery?</h2>

                  <p className='mb-4'>Under Edward Malam's ownership, the Hanley Brewery had grown over nearly 40 years into a successful local
                    business. However, his "old-fashioned habits," in which he personally collected "his own accounts, and steadily
                    refused to convert a flourishing and growing business into a limited liability company" may well have led to the
                    demise of the business after his death in 1909. (Birmingham Daily Post, 31 July 1915)</p>

                  <p className='mb-4'>Edward had neither a business partner nor family to take over the running of the Hanley Brewery. Instead, it
                    seems the business was managed by his long-standing solicitor, Thomas Robinson of Challinors and Shaw, Leek,
                    acting as trustees, until it closed in 1917. (A Century of British Brewers 1890–1990, Norman Barber, Brewery
                    History Society, 1994)</p>

                  <p className='mb-4'>Nothing remains of the Hanley Brewery today. The buildings were demolished long ago, replaced by housing. In
                    1953, several streets in Stoke were renamed. Among them was Granville Street (North), Hanley, which became
                    present-day Malam Street, named in honour of Edward Malam whose Hanley Brewery lay at the end of the street, at
                    the junction with Boundary Street.</p>

                  <EdwardRelationshipChart />

                  <p className='mb-4'><i>If anyone has any information regarding the Hanley Brewery, Edward Malam, Clarissa Bird, or any others
                    related to this story, the author would be very interested to hear from you. Please <a
                      href="mailto:john@johnmalam.co.uk?subject=Malam Family History">click here</a> to contact me.</i></p>


                  <br /><br />

                  <p className='mb-4'>Copyright &copy; 2023 John Malam. All Rights Reserved unless otherwise stated.</p>

                </>
              )}

              <nav className="edward-page-pager" aria-label="Edward Malam page navigation">
                {previousEdwardSection ? (
                  <Link to={previousEdwardSection.to}>
                    <span>Previous</span>
                    {previousEdwardSection.label}
                  </Link>
                ) : (
                  <span />
                )}
                {nextEdwardSection && (
                  <Link to={nextEdwardSection.to}>
                    <span>Next</span>
                    {nextEdwardSection.label}
                  </Link>
                )}
              </nav>

            </div>
          </div>

        </div>
        {lightboxImageSrc && (
          <div className="content-modal-backdrop" onClick={closeLightbox}>
            <div className="content-modal" onClick={(event) => event.stopPropagation()}>
              <img src={lightboxImageSrc} alt="Edward Malam enlarged" />
              <button type="button" onClick={closeLightbox} className="content-button mt-4">Close</button>
            </div>
          </div>
        )}
      </div>
    </ContentLayout>
  );
}

export default EdwardMalam;
